import React, { useState, useEffect } from 'react';
import {
    Box, Card, Grid, Typography, TextField, Button,
    Select, MenuItem, Switch, IconButton, Divider,
    Alert, Tooltip, Chip, Collapse
} from '@mui/material';
import { Add, Delete, DeleteForever, Info, ExpandMore, ExpandLess, ContentCopy } from '@mui/icons-material';

const SubscriptionPlansManager = ({ plans, onChange, onNavigateToStripeTab, creditPricing, onCreditPricingChange, registrationBonus, onRegistrationBonusChange }) => {
    const [editingPlan, setEditingPlan] = useState(null);
    const [newFeatureName, setNewFeatureName] = useState('');
    const [newFeatureType, setNewFeatureType] = useState('string');
    const [newFeatureValue, setNewFeatureValue] = useState('');
    const [showNewPlanForm, setShowNewPlanForm] = useState(false);
    const [creditPricingExpanded, setCreditPricingExpanded] = useState(false);

    // State for price variants
    const [variantsExpanded, setVariantsExpanded] = useState({});
    const [addingVariant, setAddingVariant] = useState(null);
    const [newVariant, setNewVariant] = useState({
        variantId: '',
        label: '',
        stripe_price_id: '',
        stripe_test_price_id: '',
        credits: 0,
        maxCredits: 0
    });

    // Store accumulation factor for each plan
    const [accumulationFactors, setAccumulationFactors] = useState({});

    // Initialize accumulation factors when plans load
    useEffect(() => {
        const newFactors = {};
        plans.forEach(plan => {
            if (!accumulationFactors[plan.id]) {
                // Initialize accumulation factor from current maxCredits/credits ratio
                const credits = plan.credits || 1;
                const maxCredits = plan.maxCredits || credits;
                const factor = Math.max(1, Math.round(maxCredits / credits));
                newFactors[plan.id] = factor;
            }
        });

        if (Object.keys(newFactors).length > 0) {
            setAccumulationFactors(prev => ({ ...prev, ...newFactors }));
        }
    }, [plans]);

    const [newPlan, setNewPlan] = useState({
        id: '',
        name: '',
        stripe_price_id: '',
        stripe_test_price_id: '',
        price: 0,
        currency: 'EUR',
        symbol: '€',
        credits: 0,
        maxCredits: 0,
        agents: 0,
        description: '',
        targetAudience: ''
    });

    const handlePlanChange = (index, field, value) => {
        const updatedPlans = [...plans];
        updatedPlans[index][field] = value;

        // If credits changed, recalculate maxCredits using the STORED accumulation factor
        if (field === 'credits') {
            const planId = updatedPlans[index].id;
            const factor = accumulationFactors[planId] || 1;
            updatedPlans[index].maxCredits = Math.round(value * factor);
        }

        onChange(updatedPlans);
    };

    const handleAccumulationChange = (index, factor) => {
        const updatedPlans = [...plans];
        const planId = updatedPlans[index].id;
        const credits = updatedPlans[index].credits || 0;

        // Store the accumulation factor for this plan
        setAccumulationFactors(prev => ({
            ...prev,
            [planId]: parseFloat(factor)
        }));

        // Update maxCredits
        updatedPlans[index].maxCredits = Math.round(credits * parseFloat(factor));
        onChange(updatedPlans);
    };

    const handleFeatureChange = (planIndex, featureKey, value) => {
        const updatedPlans = [...plans];
        updatedPlans[planIndex][featureKey] = value;
        onChange(updatedPlans);
    };

    const handleDeleteFeature = (planIndex, featureKey) => {
        const updatedPlans = [...plans];
        delete updatedPlans[planIndex][featureKey];
        onChange(updatedPlans);
    };

    const handleAddFeature = (planIndex) => {
        if (!newFeatureName) return;

        const updatedPlans = [...plans];
        updatedPlans[planIndex][newFeatureName] = newFeatureType === 'boolean' ? false : newFeatureValue || '';
        onChange(updatedPlans);

        // Reset form
        setNewFeatureName('');
        setNewFeatureValue('');
        setNewFeatureType('string');
        setEditingPlan(null);
    };

    const handleAddNewPlan = () => {
        if (newPlan.id && newPlan.name) {
            // Ensure maxCredits is set properly (default to 1x if not set)
            const planToAdd = {
                ...newPlan,
                maxCredits: newPlan.maxCredits || newPlan.credits
            };
            const updatedPlans = [...plans, planToAdd];
            onChange(updatedPlans);
            setNewPlan({
                id: '',
                name: '',
                stripe_price_id: '',
                stripe_test_price_id: '',
                price: 0,
                currency: 'EUR',
                symbol: '€',
                credits: 0,
                maxCredits: 0,
                agents: 0,
                description: '',
                targetAudience: ''
            });
            setShowNewPlanForm(false);
        }
    };

    const handleDeletePlan = (index) => {
        const updatedPlans = plans.filter((_, i) => i !== index);
        onChange(updatedPlans);
    };

    // Variant handlers
    const handleAddVariant = (planIndex) => {
        if (!newVariant.variantId || !newVariant.label) return;

        const updatedPlans = [...plans];
        const plan = updatedPlans[planIndex];

        // Initialize priceVariants array if it doesn't exist
        if (!plan.priceVariants) {
            plan.priceVariants = [];
        }

        // Check for duplicate variantId
        if (plan.priceVariants.some(v => v.variantId === newVariant.variantId)) {
            alert('Variant ID already exists for this plan');
            return;
        }

        plan.priceVariants.push({
            ...newVariant,
            maxCredits: newVariant.maxCredits || newVariant.credits
        });

        onChange(updatedPlans);

        // Reset form
        setNewVariant({
            variantId: '',
            label: '',
            stripe_price_id: '',
            stripe_test_price_id: '',
            credits: 0,
            maxCredits: 0
        });
        setAddingVariant(null);
    };

    const handleVariantChange = (planIndex, variantIndex, field, value) => {
        const updatedPlans = [...plans];
        updatedPlans[planIndex].priceVariants[variantIndex][field] = value;

        // If credits changed, update maxCredits if it's not set or less than credits
        if (field === 'credits') {
            const variant = updatedPlans[planIndex].priceVariants[variantIndex];
            if (!variant.maxCredits || variant.maxCredits < value) {
                variant.maxCredits = value;
            }
        }

        onChange(updatedPlans);
    };

    const handleDeleteVariant = (planIndex, variantIndex) => {
        const updatedPlans = [...plans];
        updatedPlans[planIndex].priceVariants.splice(variantIndex, 1);

        // Remove empty array
        if (updatedPlans[planIndex].priceVariants.length === 0) {
            delete updatedPlans[planIndex].priceVariants;
        }

        onChange(updatedPlans);
    };

    const copyVariantUrl = (planId, variantId) => {
        const url = `${window.location.origin}/subscriptions?plan=${planId}&variant=${variantId}`;
        navigator.clipboard.writeText(url);
    };

    const requiredFields = ['id', 'name', 'stripe_price_id', 'stripe_test_price_id', 'price', 'currency', 'symbol', 'credits', 'maxCredits', 'agents', 'description', 'targetAudience', 'priceVariants'];

    // Default pricing if not set
    const defaultCreditPricing = {
        creditsPer10Units: 500, // 50 credits per €1 (50% markup from OpenKBS)
        currency: 'EUR',
        symbol: '€',
        disabled: true
    };

    const pricing = { ...defaultCreditPricing, ...creditPricing };
    const isCreditPricingDisabled = pricing.disabled === true;

    return (
        <Box>
            {/* Setup Instructions */}
            <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Setup Instructions:
                </Typography>
                <Typography variant="body2" component="div">
                    <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>
                        <li>First, create your subscription products in your Stripe Dashboard (both Live and Test modes)</li>
                        <li>For each plan, create a recurring price and copy the Price ID (starts with price_)</li>
                        <li>Enter both Production and Test Price IDs in the corresponding plan below</li>
                        <li>Set the credits amount (100 credits = €1)</li>
                        <li>Configure accumulation limit (how many credits can carry over to next month)</li>
                        <li>Save your changes</li>
                        <li>Go to the{' '}
                            <span
                                style={{
                                    color: '#1976d2',
                                    textDecoration: 'underline',
                                    cursor: 'pointer'
                                }}
                                onClick={onNavigateToStripeTab}
                            >
                                Stripe Integration
                            </span>
                            {' '}tab to configure your Stripe keys and webhooks
                        </li>
                    </ol>
                </Typography>
            </Alert>

            {/* Registration Bonus Settings */}
            <Card sx={{ mb: 3, p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Registration Bonus
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        These credits will be transferred from your account.
                        Make sure you have sufficient credits available.
                    </Typography>
                </Alert>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Registration Bonus Credits"
                            type="number"
                            value={registrationBonus?.credits !== undefined ? registrationBonus.credits : 500}
                            onChange={(e) => onRegistrationBonusChange({
                                ...registrationBonus,
                                credits: Number(e.target.value)
                            })}
                            helperText="Credits given to new users on registration"
                        />
                    </Grid>
                </Grid>
            </Card>

            {/* Credits Direct Purchase Pricing */}
            <Card sx={{ mb: 3, p: 2, backgroundColor: isCreditPricingDisabled ? '#f5f5f5' : undefined }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        One-Time Credit Purchases
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            {isCreditPricingDisabled ? 'Disabled' : 'Enabled'}
                        </Typography>
                        <Switch
                            checked={!isCreditPricingDisabled}
                            onChange={(e) => {
                                const enabled = e.target.checked;
                                onCreditPricingChange({
                                    ...pricing,
                                    disabled: !enabled
                                });
                                if (enabled) {
                                    setCreditPricingExpanded(true);
                                }
                            }}
                            color="primary"
                        />
                    </Box>
                </Box>

                {!isCreditPricingDisabled && (
                    <>
                        {!creditPricingExpanded && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setCreditPricingExpanded(true)}
                                sx={{ mb: 2 }}
                            >
                                Configure Pricing
                            </Button>
                        )}

                        {creditPricingExpanded && (
                            <>
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    <Typography variant="body2">
                                        Configure how many credits customers receive when purchasing credits directly (one-time payment).
                                        <br />
                                        OpenKBS provides 1000 credits for {pricing.symbol}10. You can set your own pricing with markup.
                                    </Typography>
                                </Alert>

                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            label={`Credits per ${pricing.symbol}10`}
                                            type="number"
                                            value={pricing.creditsPer10Units || 500}
                                            onChange={(e) => onCreditPricingChange({
                                                ...pricing,
                                                creditsPer10Units: Number(e.target.value)
                                            })}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={2}>
                                        <Select
                                            fullWidth
                                            value={pricing.currency || 'EUR'}
                                            onChange={(e) => {
                                                const currency = e.target.value;
                                                const symbol = currency === 'USD' ? '$' : '€';
                                                onCreditPricingChange({
                                                    ...pricing,
                                                    currency,
                                                    symbol
                                                });
                                            }}
                                        >
                                            <MenuItem value="EUR">EUR (€)</MenuItem>
                                            <MenuItem value="USD">USD ($)</MenuItem>
                                        </Select>
                                    </Grid>

                                    <Grid item xs={12} md={7}>
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            <Chip
                                                label={`${pricing.symbol}1 = ${(pricing.creditsPer10Units || 500) / 10} credits`}
                                                color="primary"
                                                variant="outlined"
                                            />
                                            {/* Only show markup and profit for EUR since we don't have exchange rates */}
                                            {pricing.currency === 'EUR' && (
                                                <>
                                                    <Chip
                                                        label={`Markup: ${Math.round((1000 / (pricing.creditsPer10Units || 500) - 1) * 100)}%`}
                                                        color={(pricing.creditsPer10Units || 500) < 1000 ? 'success' : 'warning'}
                                                        variant="outlined"
                                                    />
                                                    <Tooltip title="OpenKBS charges you €10 for 1000 credits">
                                                        <Chip
                                                            label={`Your profit: €${((10 - (10 * (pricing.creditsPer10Units || 500) / 1000))).toFixed(2)} per €10`}
                                                            color="success"
                                                        />
                                                    </Tooltip>
                                                </>
                                            )}
                                            {pricing.currency === 'USD' && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                                                    (Profit calculation not available for USD)
                                                </Typography>
                                            )}
                                        </Box>
                                    </Grid>
                                </Grid>

                                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="text"
                                        size="small"
                                        onClick={() => setCreditPricingExpanded(false)}
                                    >
                                        Collapse
                                    </Button>
                                </Box>
                            </>
                        )}
                    </>
                )}

                {isCreditPricingDisabled && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        <Typography variant="body2">
                            One-time credit purchases are disabled. Users will only be able to get credits through subscription plans.
                        </Typography>
                    </Alert>
                )}
            </Card>

            <Typography variant="body2" color="text.secondary" gutterBottom>
                Configure subscription plans for your customers. Each plan must have a corresponding product in Stripe.
            </Typography>

            {/* Plans List */}
            {plans.map((plan, index) => (
                <Card key={index} sx={{ mb: 2, mt: 2, p: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        label="Plan ID"
                                        value={plan.id}
                                        onChange={(e) => handlePlanChange(index, 'id', e.target.value)}
                                        size="small"
                                        sx={{ minWidth: 150 }}
                                    />
                                    <TextField
                                        label="Plan Name"
                                        value={plan.name || ''}
                                        onChange={(e) => handlePlanChange(index, 'name', e.target.value)}
                                        size="small"
                                        sx={{ minWidth: 200 }}
                                    />
                                </Box>
                                <IconButton
                                    color="error"
                                    onClick={() => handleDeletePlan(index)}
                                    title="Delete Plan"
                                >
                                    <DeleteForever />
                                </IconButton>
                            </Box>
                        </Grid>

                        {/* Required Fields */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Stripe Price ID (Production)"
                                value={plan.stripe_price_id}
                                onChange={(e) => handlePlanChange(index, 'stripe_price_id', e.target.value)}
                                placeholder="price_R1aOSi09b3JzQl2iabUg6r0m"
                                helperText="From Stripe dashboard (Live mode)"
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Stripe Test Price ID"
                                value={plan.stripe_test_price_id || ''}
                                onChange={(e) => handlePlanChange(index, 'stripe_test_price_id', e.target.value)}
                                placeholder="price_TEST_1aOSi09b3JzQl2i"
                                helperText="From Stripe dashboard (Test mode)"
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={1}>
                            <TextField
                                fullWidth
                                label="Price"
                                value={plan.price}
                                onChange={(e) => handlePlanChange(index, 'price', e.target.value)}
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={1.5}>
                            <TextField
                                fullWidth
                                label="Credits"
                                type="number"
                                value={plan.credits}
                                onChange={(e) => handlePlanChange(index, 'credits', Number(e.target.value))}
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={3.5}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Select
                                    value={String(accumulationFactors[plan.id] || 1)}
                                    onChange={(e) => handleAccumulationChange(index, e.target.value)}
                                    size="small"
                                    sx={{ minWidth: 120 }}
                                >
                                    <MenuItem value="1">1x (no carry)</MenuItem>
                                    <MenuItem value="1.5">1.5x</MenuItem>
                                    <MenuItem value="2">2x</MenuItem>
                                    <MenuItem value="3">3x</MenuItem>
                                    <MenuItem value="4">4x</MenuItem>
                                    <MenuItem value="5">5x</MenuItem>
                                    <MenuItem value="6">6x</MenuItem>
                                    <MenuItem value="8">8x</MenuItem>
                                    <MenuItem value="10">10x</MenuItem>
                                    <MenuItem value="12">12x</MenuItem>
                                </Select>
                                <TextField
                                    label="Max Credits"
                                    type="number"
                                    value={plan.maxCredits || plan.credits || 0}
                                    disabled
                                    size="small"
                                    sx={{ width: 120 }}
                                />
                                <Tooltip title="Maximum credits that can accumulate. Automatically calculated as Credits × Accumulation Factor">
                                    <Info fontSize="small" color="action" />
                                </Tooltip>
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={1}>
                            <TextField
                                fullWidth
                                label="Agents"
                                type="number"
                                value={plan.agents}
                                onChange={(e) => handlePlanChange(index, 'agents', Number(e.target.value))}
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={1}>
                            <TextField
                                fullWidth
                                label="Currency"
                                value={plan.currency}
                                onChange={(e) => handlePlanChange(index, 'currency', e.target.value)}
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12} sm={1}>
                            <TextField
                                fullWidth
                                label="Symbol"
                                value={plan.symbol}
                                onChange={(e) => handlePlanChange(index, 'symbol', e.target.value)}
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description"
                                value={plan.description}
                                onChange={(e) => handlePlanChange(index, 'description', e.target.value)}
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Target Audience"
                                value={plan.targetAudience}
                                onChange={(e) => handlePlanChange(index, 'targetAudience', e.target.value)}
                                size="small"
                            />
                        </Grid>

                        {/* Features Section */}
                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                                Plan Features
                            </Typography>
                        </Grid>

                        {/* Display existing features */}
                        {Object.entries(plan).filter(([key]) =>
                            !requiredFields.includes(key)
                        ).map(([key, value]) => (
                            <Grid item xs={12} sm={6} key={key}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {typeof value === 'boolean' ? (
                                        <>
                                            <Switch
                                                color={"secondary"}
                                                checked={value}
                                                onChange={(e) => handleFeatureChange(index, key, e.target.checked)}
                                            />
                                            <Typography variant="body2">
                                                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                            </Typography>
                                        </>
                                    ) : (
                                        <TextField
                                            fullWidth
                                            label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                            value={value}
                                            onChange={(e) => handleFeatureChange(index, key, e.target.value)}
                                            size="small"
                                        />
                                    )}
                                    <IconButton
                                        size="small"
                                        onClick={() => handleDeleteFeature(index, key)}
                                    >
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Grid>
                        ))}

                        {/* Add new feature */}
                        {editingPlan === plan.id ? (
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                    <TextField
                                        label="Feature Name"
                                        value={newFeatureName}
                                        onChange={(e) => setNewFeatureName(e.target.value)}
                                        size="small"
                                        placeholder="e.g., maxUsers"
                                    />
                                    <Select
                                        value={newFeatureType}
                                        onChange={(e) => setNewFeatureType(e.target.value)}
                                        size="small"
                                        sx={{ minWidth: 120 }}
                                    >
                                        <MenuItem value="boolean">Boolean</MenuItem>
                                        <MenuItem value="string">String</MenuItem>
                                    </Select>
                                    {newFeatureType === 'string' && (
                                        <TextField
                                            label="Value"
                                            value={newFeatureValue}
                                            onChange={(e) => setNewFeatureValue(e.target.value)}
                                            size="small"
                                        />
                                    )}
                                    <Button
                                        variant="contained"
                                        onClick={() => handleAddFeature(index)}
                                        disabled={!newFeatureName}
                                    >
                                        Add
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            setEditingPlan(null);
                                            setNewFeatureName('');
                                            setNewFeatureValue('');
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            </Grid>
                        ) : (
                            <Grid item xs={12}>
                                <Button
                                    startIcon={<Add />}
                                    size="small"
                                    onClick={() => setEditingPlan(plan.id)}
                                >
                                    Add Feature
                                </Button>
                            </Grid>
                        )}

                        {/* Price Variants Section */}
                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Box
                                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mt: 1 }}
                                onClick={() => setVariantsExpanded(prev => ({ ...prev, [plan.id]: !prev[plan.id] }))}
                            >
                                {variantsExpanded[plan.id] ? <ExpandLess /> : <ExpandMore />}
                                <Typography variant="subtitle2" sx={{ ml: 0.5 }}>
                                    Price Variants
                                    {plan.priceVariants?.length > 0 && (
                                        <Chip
                                            label={plan.priceVariants.length}
                                            size="small"
                                            sx={{ ml: 1, height: 20 }}
                                        />
                                    )}
                                </Typography>
                                <Tooltip title="Create different price variants (legacy, partner, promotional) with different credits for the same plan">
                                    <Info fontSize="small" color="action" sx={{ ml: 1 }} />
                                </Tooltip>
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <Collapse in={variantsExpanded[plan.id]}>
                                <Box sx={{ pl: 2, pr: 2, pb: 1 }}>
                                    {/* Existing Variants */}
                                    {plan.priceVariants?.map((variant, variantIndex) => (
                                        <Card key={variant.variantId} sx={{ p: 1.5, mb: 1, backgroundColor: '#f9f9f9' }}>
                                            <Grid container spacing={1} alignItems="center">
                                                <Grid item xs={12} sm={2}>
                                                    <TextField
                                                        fullWidth
                                                        label="Variant ID"
                                                        value={variant.variantId}
                                                        onChange={(e) => handleVariantChange(index, variantIndex, 'variantId', e.target.value)}
                                                        size="small"
                                                        disabled
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={2}>
                                                    <TextField
                                                        fullWidth
                                                        label="Label"
                                                        value={variant.label}
                                                        onChange={(e) => handleVariantChange(index, variantIndex, 'label', e.target.value)}
                                                        size="small"
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={2.5}>
                                                    <TextField
                                                        fullWidth
                                                        label="Stripe Price ID (Prod)"
                                                        value={variant.stripe_price_id || ''}
                                                        onChange={(e) => handleVariantChange(index, variantIndex, 'stripe_price_id', e.target.value)}
                                                        size="small"
                                                        placeholder="price_xxx"
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={2.5}>
                                                    <TextField
                                                        fullWidth
                                                        label="Stripe Price ID (Test)"
                                                        value={variant.stripe_test_price_id || ''}
                                                        onChange={(e) => handleVariantChange(index, variantIndex, 'stripe_test_price_id', e.target.value)}
                                                        size="small"
                                                        placeholder="price_test_xxx"
                                                    />
                                                </Grid>
                                                <Grid item xs={6} sm={1}>
                                                    <TextField
                                                        fullWidth
                                                        label="Credits"
                                                        type="number"
                                                        value={variant.credits || 0}
                                                        onChange={(e) => handleVariantChange(index, variantIndex, 'credits', Number(e.target.value))}
                                                        size="small"
                                                    />
                                                </Grid>
                                                <Grid item xs={6} sm={1}>
                                                    <TextField
                                                        fullWidth
                                                        label="Max"
                                                        type="number"
                                                        value={variant.maxCredits || variant.credits || 0}
                                                        onChange={(e) => handleVariantChange(index, variantIndex, 'maxCredits', Number(e.target.value))}
                                                        size="small"
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={1}>
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <Tooltip title="Copy checkout URL">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => copyVariantUrl(plan.id, variant.variantId)}
                                                            >
                                                                <ContentCopy fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleDeleteVariant(index, variantIndex)}
                                                        >
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Card>
                                    ))}

                                    {/* Add New Variant */}
                                    {addingVariant === plan.id ? (
                                        <Card sx={{ p: 1.5, mb: 1, backgroundColor: '#e8f4fd' }}>
                                            <Grid container spacing={1} alignItems="center">
                                                <Grid item xs={12} sm={2}>
                                                    <TextField
                                                        fullWidth
                                                        label="Variant ID *"
                                                        value={newVariant.variantId}
                                                        onChange={(e) => setNewVariant({ ...newVariant, variantId: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                                                        size="small"
                                                        placeholder="e.g., legacy-2024"
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={2}>
                                                    <TextField
                                                        fullWidth
                                                        label="Label *"
                                                        value={newVariant.label}
                                                        onChange={(e) => setNewVariant({ ...newVariant, label: e.target.value })}
                                                        size="small"
                                                        placeholder="e.g., Legacy 2024"
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={2.5}>
                                                    <TextField
                                                        fullWidth
                                                        label="Stripe Price ID (Prod)"
                                                        value={newVariant.stripe_price_id}
                                                        onChange={(e) => setNewVariant({ ...newVariant, stripe_price_id: e.target.value })}
                                                        size="small"
                                                        placeholder="price_xxx"
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={2.5}>
                                                    <TextField
                                                        fullWidth
                                                        label="Stripe Price ID (Test)"
                                                        value={newVariant.stripe_test_price_id}
                                                        onChange={(e) => setNewVariant({ ...newVariant, stripe_test_price_id: e.target.value })}
                                                        size="small"
                                                        placeholder="price_test_xxx"
                                                    />
                                                </Grid>
                                                <Grid item xs={6} sm={1}>
                                                    <TextField
                                                        fullWidth
                                                        label="Credits"
                                                        type="number"
                                                        value={newVariant.credits}
                                                        onChange={(e) => setNewVariant({ ...newVariant, credits: Number(e.target.value) })}
                                                        size="small"
                                                    />
                                                </Grid>
                                                <Grid item xs={6} sm={1}>
                                                    <TextField
                                                        fullWidth
                                                        label="Max"
                                                        type="number"
                                                        value={newVariant.maxCredits}
                                                        onChange={(e) => setNewVariant({ ...newVariant, maxCredits: Number(e.target.value) })}
                                                        size="small"
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={1}>
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            onClick={() => handleAddVariant(index)}
                                                            disabled={!newVariant.variantId || !newVariant.label}
                                                        >
                                                            Add
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            onClick={() => {
                                                                setAddingVariant(null);
                                                                setNewVariant({
                                                                    variantId: '',
                                                                    label: '',
                                                                    stripe_price_id: '',
                                                                    stripe_test_price_id: '',
                                                                    credits: 0,
                                                                    maxCredits: 0
                                                                });
                                                            }}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Card>
                                    ) : (
                                        <Button
                                            startIcon={<Add />}
                                            size="small"
                                            onClick={() => setAddingVariant(plan.id)}
                                        >
                                            Add Price Variant
                                        </Button>
                                    )}

                                    <Alert severity="info" sx={{ mt: 1 }}>
                                        <Typography variant="caption">
                                            Price variants allow different Stripe prices with different credits for the same plan.
                                            Use for legacy pricing, partner deals, or promotional offers.
                                            Subscribe via: <code>/subscriptions?plan={plan.id}&variant=VARIANT_ID</code>
                                        </Typography>
                                    </Alert>
                                </Box>
                            </Collapse>
                        </Grid>
                    </Grid>
                </Card>
            ))}


            {/* Add New Plan Button */}
            {!showNewPlanForm && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<Add sx={{ color: 'white' }} />}
                        onClick={() => setShowNewPlanForm(true)}
                    >
                        Add New Plan
                    </Button>
                </Box>
            )}

            {/* New Plan Form */}
            {showNewPlanForm && (
                <Card sx={{ mt: 3, p: 2, backgroundColor: '#f0f8ff' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>New Subscription Plan</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={2}>
                            <TextField
                                fullWidth
                                label="Plan ID *"
                                value={newPlan.id}
                                onChange={(e) => setNewPlan({ ...newPlan, id: e.target.value })}
                                placeholder="e.g., premium"
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                fullWidth
                                label="Plan Name *"
                                value={newPlan.name}
                                onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })}
                                placeholder="e.g., Premium"
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                label="Stripe Price ID"
                                value={newPlan.stripe_price_id}
                                onChange={(e) => setNewPlan({ ...newPlan, stripe_price_id: e.target.value })}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={1}>
                            <TextField
                                fullWidth
                                label="Price"
                                type="number"
                                value={newPlan.price}
                                onChange={(e) => setNewPlan({ ...newPlan, price: Number(e.target.value) })}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={1.5}>
                            <TextField
                                fullWidth
                                label="Credits"
                                type="number"
                                value={newPlan.credits}
                                onChange={(e) => setNewPlan({ ...newPlan, credits: Number(e.target.value) })}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={1}>
                            <TextField
                                fullWidth
                                label="Agents"
                                type="number"
                                value={newPlan.agents}
                                onChange={(e) => setNewPlan({ ...newPlan, agents: Number(e.target.value) })}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={1}>
                            <TextField
                                fullWidth
                                label="Currency"
                                value={newPlan.currency}
                                onChange={(e) => setNewPlan({ ...newPlan, currency: e.target.value })}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={1}>
                            <TextField
                                fullWidth
                                label="Symbol"
                                value={newPlan.symbol}
                                onChange={(e) => setNewPlan({ ...newPlan, symbol: e.target.value })}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description"
                                value={newPlan.description}
                                onChange={(e) => setNewPlan({ ...newPlan, description: e.target.value })}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Target Audience"
                                value={newPlan.targetAudience}
                                onChange={(e) => setNewPlan({ ...newPlan, targetAudience: e.target.value })}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setShowNewPlanForm(false);
                                        setNewPlan({
                                            id: '',
                                            stripe_price_id: '',
                                            price: 0,
                                            currency: 'EUR',
                                            symbol: '€',
                                            credits: 0,
                                            agents: 0,
                                            description: '',
                                            targetAudience: ''
                                        });
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleAddNewPlan}
                                    disabled={!newPlan.id || !newPlan.name}
                                >
                                    Add Plan
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Card>
            )}
        </Box>
    );
};

export default SubscriptionPlansManager;